import { useEffect } from 'react'
import { useWebsiteStore } from '../store/useWebsiteStore'

/**
 * Undo and redo from the keyboard.
 *
 * Ignored while the focus is in a field, because there the browser's own undo is
 * what the person means: taking Ctrl+Z away from a half-typed heading to revert
 * a row they changed a minute ago is the most annoying possible reading of the
 * shortcut.
 */
const isTypingTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) return false
  const tag = target.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable
}

export function useEditorShortcuts() {
  const undo = useWebsiteStore(s => s.undo)
  const redo = useWebsiteStore(s => s.redo)

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey)) return
      if (event.key.toLowerCase() !== 'z') return
      if (isTypingTarget(event.target)) return

      event.preventDefault()
      if (event.shiftKey) redo()
      else undo()
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [undo, redo])
}
